import { invert } from 'lodash';
import {
  BACKEND_EVENT_NAME,
  RULE_TYPES,
  SYSTEM_EVENTS_METADATA,
} from 'modules/journey/components/RuleBuilder/constants';
import {
  RuleType,
  Rules,
  SearchQuery,
  ServerReportedCloningError,
} from 'modules/journey/components/RuleBuilder/types';
import { v4 as uuidv4 } from 'uuid';

/*
triggerRule in nodeData:

{"triggerRule": {
    "triggerRuleType": "CustomEventRule",
    "eventName": "nishant_broke_prod",
    "id": "ee451d3a-c448-11ed-afa1-0242ac120002",
    "searchQuery": {
        "combinator": "And",
        "searchQueries": [
        {
            "dataType": "customEvent",
            "comparatorType": "Equals",
            "field": "nishant_broke_prod.foo",
            "fieldType": "string",
            "value": "foo",
            "id": "nishant_broke_prod.foo",
            "label": "nishant_broke_prod.foo",
            "labelInMenu": "nishant_broke_prod.foo",
            "labelInTriggerButton": "nishant_broke_prod.foo",
            "categoryId": "default",
            "uniqueId": "eventName-0-property-select-input-nishant_broke_prod.foo"
        }
        ]
    }
}}

ruleBuilder transform:
[{
    id: 'ee451d3a-c448-11ed-afa1-0242ac120002',
    combinator: 'And',
    childRules: [{
        ruleType: 'CustomEventRule',
        eventName: 'nishant_broke_prod',
        id: 'ee451d3a-c448-11ed-afa1-0242ac120002',
        searchQuery: {
            combinator: 'And',
            searchQueries: [{
                dataType: 'customEvent',
                comparatorType: 'Equals',
                field: 'nishant_broke_prod.foo',
                fieldType: 'string',
                value: 'foo',
                id: 'nishant_broke_prod.foo',
                label: 'nishant_broke_prod.foo',
                labelInMenu: 'nishant_broke_prod.foo',
                labelInTriggerButton: 'nishant_broke_prod.foo',
                categoryId: 'default',
                uniqueId: 'eventName-0-property-select-input-nishant_broke_prod.foo'
            }]
        }
    }]
}]
*/

export type TriggerRule = {
  triggerRuleType: string;
  eventName?: string;
  field?: string;
  id?: string;
  searchQuery: SearchQuery;
  cloningErrorsList?: ServerReportedCloningError[];
};
export type TriggerRuleType =
  | 'CustomEventRule'
  | 'SystemEventRule'
  | 'UserPropertyRule'
  | 'RuleCombo';

export const TRIGGER_RULE_TYPES = {
  CUSTOM_EVENT: 'CustomEventRule',
  SYSTEM_EVENT: 'SystemEventRule',
  USER_PROPERTY: 'UserPropertyRule',
  COMBO_RULE: 'RuleCombo',
} as Record<string, TriggerRuleType>;

const HOLD_RULE_TYPES = {
  ADDED_ITEM_TO_CART: 'AddedItemToCartRule',
  PURCHASED: 'PurchasedRule',
  // will add List Subscribe/Unsubscribe when we're ready to support them
  SUBSCRIBED_TO_LIST: 'SubscribedRule',
  UNSUBSCRIBED_FROM_LIST: 'UnsubscribedRule',
  OPENED_AN_EMAIL: 'OpenedAnEmailRule',
  CLICKED_AN_EMAIL: 'ClickedAnEmailRule',
  RECEIVED_AN_SMS: 'ReceivedAnSMSRule',
  // OpenedPushTrigger: 'OpenedPush',
  CLICKED_AN_IN_APP: 'ClickedAnInAppRule',
  OPENED_AN_IN_APP: 'OpenedAnInAppRule',
  FIELD_CHANGED: 'FieldChangedRule',
  WHATS_APP_CLICK: 'ClickedWhatsAppRule',
  WHATS_APP_RECEIVED: 'ReceivedWhatsAppRule',
  SEEN_WHATS_APP: 'SeenWhatsAppRule',
};

export const TRIGGER_RULE_TYPES_TO_RULE_TYPES = {
  [TRIGGER_RULE_TYPES.CUSTOM_EVENT]: RULE_TYPES.CUSTOM_EVENT,
  [TRIGGER_RULE_TYPES.SYSTEM_EVENT]: RULE_TYPES.SYSTEM_EVENT,
  [HOLD_RULE_TYPES.FIELD_CHANGED]: RULE_TYPES.USER_PROPERTY,
  [TRIGGER_RULE_TYPES.COMBO_RULE]: RULE_TYPES.COMBO_RULE,
};

export const RULE_TYPES_TO_TRIGGER_RULE_TYPES = {
  [RULE_TYPES.CUSTOM_EVENT]: TRIGGER_RULE_TYPES.CUSTOM_EVENT,
  [RULE_TYPES.SYSTEM_EVENT]: TRIGGER_RULE_TYPES.SYSTEM_EVENT,
  [RULE_TYPES.USER_PROPERTY]: HOLD_RULE_TYPES.FIELD_CHANGED,
  [RULE_TYPES.COMBO_RULE]: TRIGGER_RULE_TYPES.COMBO_RULE,
};

const DATA_TYPE_TO_RULE_MAP = {
  updateCart: HOLD_RULE_TYPES.ADDED_ITEM_TO_CART,
  purchase: HOLD_RULE_TYPES.PURCHASED,
  emailSubscribe: HOLD_RULE_TYPES.SUBSCRIBED_TO_LIST,
  emailUnSubscribe: HOLD_RULE_TYPES.UNSUBSCRIBED_FROM_LIST,
  fieldChange: 'FieldChangedRule',
  emailOpen: HOLD_RULE_TYPES.OPENED_AN_EMAIL,
  emailClick: HOLD_RULE_TYPES.CLICKED_AN_EMAIL,
  emailSend: 'SentAnEmailRule',
  smsReceived: HOLD_RULE_TYPES.RECEIVED_AN_SMS,
  pushOpen: 'OpenedPushRule',
  inAppClick: HOLD_RULE_TYPES.CLICKED_AN_IN_APP,
  inAppClose: 'ClosedAnInAppRule',
  inAppDelete: 'DeletedAnInAppRule',
  inAppDelivery: 'DeliveredAnInAppRule',
  inAppOpen: HOLD_RULE_TYPES.OPENED_AN_IN_APP,
  whatsAppClick: HOLD_RULE_TYPES.WHATS_APP_CLICK,
  whatsAppReceived: HOLD_RULE_TYPES.WHATS_APP_RECEIVED,
  whatsAppSeen: HOLD_RULE_TYPES.SEEN_WHATS_APP,
} as Record<string, string>;

function getRuleFromEventName(eventName?: string): string | undefined {
  if (!eventName) return undefined;

  const EVENT_NAME_TO_TRIGGER_MAP = invert(BACKEND_EVENT_NAME);
  const triggerName = EVENT_NAME_TO_TRIGGER_MAP[eventName];

  const dataType = SYSTEM_EVENTS_METADATA[triggerName]?.dataType;
  if (dataType) {
    return DATA_TYPE_TO_RULE_MAP[dataType];
  } else {
    return undefined;
  }
}

function getTriggerNameFromDataType(dataType: string) {
  let foundKey;
  Object.entries(SYSTEM_EVENTS_METADATA).forEach(([key, value]) => {
    if (value.dataType === dataType) {
      foundKey = key;
    }
  });

  return foundKey;
}

// as opposed to Exit Rules which stores the event name on eventType for System Events,
// we have to use the ruleType, which requires several mappings.
function getEventNameFromRuleType(ruleType: string) {
  // not very fun.
  const dataType = invert(DATA_TYPE_TO_RULE_MAP)[ruleType];
  const triggerName = getTriggerNameFromDataType(dataType);
  return triggerName ? BACKEND_EVENT_NAME[triggerName] : undefined;
}

export function transformTriggerRulesToRuleBlockRules(triggerRules: TriggerRule[]): Rules {
  return triggerRules.map((triggerRule) => {
    const { eventName, triggerRuleType, id, searchQuery, field } = triggerRule;
    return {
      id,
      combinator: 'And',
      childRules: [
        {
          ruleType:
            (TRIGGER_RULE_TYPES_TO_RULE_TYPES[triggerRuleType] as RuleType) ??
            (RULE_TYPES.SYSTEM_EVENT as RuleType),
          id,
          searchQuery,
          eventName: getEventNameFromRuleType(triggerRuleType) ?? eventName,
          userField: field,
        },
      ],
    };
  });
}

export function transformRulesToTriggerRules(rules: Rules): TriggerRule {
  const rule = rules[0];
  const { childRules, id } = rule;
  const { ruleType, searchQuery, eventName, userField } = childRules[0];
  const triggerRuleType =
    getRuleFromEventName(eventName) ?? RULE_TYPES_TO_TRIGGER_RULE_TYPES[ruleType];

  return {
    triggerRuleType,
    searchQuery,
    eventName,
    field: userField,
    id: id ?? uuidv4(), // BE doesn't gen the ID's, so we do it here
  };
}
